import { AttachmentItem } from '@/components_v2/AttachmentItem/AttachmentItem';
import { Dropzone } from '@/components_v2/Dropzone';
import { PhotoAnnotatorModal } from '@/components_v2/PhotoAnnotator';
import { useModals } from '@/hooks/ModalManager';
import { CreateAttachmentParams } from '@/modules/attachments/services';
import { ItemAttachment } from '@/modules/core/domain/interfaces/ItemAttachment';
import { blobToFile } from '@/utils/blobToFile';
import { readFile } from '@/utils/readFile';
import { Flex, List, ListItem, Spinner } from '@chakra-ui/react';
import { BatchItem, FILE_STATES } from '@rpldy/shared';
import {
  useBatchAddListener,
  useItemFinishListener,
  useUploady,
} from '@rpldy/uploady';
import React from 'react';
import { useTranslation } from 'react-i18next';

export type UseQuestionAttachmentUploader = () => {
  onDelete: (id: string) => void;
  onCreate: (
    payload: Omit<CreateAttachmentParams, 'itemId' | 'questionId'>,
  ) => Promise<void>;
  onUploadStart: () => void;
  items: ItemAttachment[];
  isLoading: boolean;
};

type QuestionAttachmentsProps = {
  useUploader: UseQuestionAttachmentUploader;
};

export const QuestionAttachments: React.FC<QuestionAttachmentsProps> = ({
  useUploader,
}) => {
  const { t } = useTranslation();
  const modals = useModals();

  const { items, isLoading, onCreate, onDelete, onUploadStart } = useUploader();

  const uploady = useUploady();

  const [files, setFiles] = React.useState<BatchItem[]>([]);
  const [annotationQueue, setAnnotationQueue] = React.useState<File[]>([]);
  const [currentFileUrl, setCurrentFileUrl] = React.useState<string | null>(
    null,
  );
  const currentFile = annotationQueue[0] ?? null;

  React.useEffect(() => {
    if (!currentFile) {
      setCurrentFileUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(currentFile);
    setCurrentFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [currentFile]);

  useBatchAddListener((batch) => {
    setFiles((items) => items.concat(batch.items));
  });

  useItemFinishListener(async (item) => {
    if (item.state !== FILE_STATES.FINISHED) return;

    const fileExt = item.file.name.split('.').pop();
    await onCreate({
      bytes: item.file.size,
      name: item.file.name.replace(/ /g, '_'),
      extension: fileExt || '',
      mimeType: item.file.type,
      failCount: 0,
      localUrl: '',
      remoteUrl: `${item.uploadResponse}`,
      status: 'done',
    });
    setFiles((old) => old.filter((f) => f.id !== item.id));
  });

  const confirmDelete = React.useCallback(
    async (id: string) => {
      modals.confirm({
        title: t('common.delete_attachment_confirm'),
        body: t('common.action_cannot_be_undone'),
        portalProps: {
          appendToParentPortal: false,
        },
        onConfirm: () => {
          onDelete(id);
        },
      });
    },
    [modals, onDelete],
  );

  const renderUploadingItem = React.useCallback((file: BatchItem) => {
    return (
      <ListItem key={file.id}>
        <AttachmentItem
          bytes={file.file.size}
          id={file.id}
          extension={file.file.type}
          isUploading
          name={file.file.name}
          mimeType={file.file.type}
          url={URL.createObjectURL(file.file as File)}
        />
      </ListItem>
    );
  }, []);

  const renderItem = React.useCallback(
    (item: ItemAttachment) => {
      return (
        <ListItem key={item.id}>
          <a href={item.remoteUrl} target="_blank" rel="noopener noreferrer">
            <AttachmentItem
              flexProps={{ bg: 'white' }}
              bytes={item.bytes}
              id={item.id}
              extension={item.extension}
              name={item.name}
              mimeType={item.mimeType}
              onDelete={() => confirmDelete(item.id)}
              url={item.remoteUrl}
            />
          </a>
        </ListItem>
      );
    },
    [confirmDelete],
  );

  const handleDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      const images = acceptedFiles.filter((file) =>
        file.type.startsWith('image/'),
      );
      const others = acceptedFiles.filter(
        (file) => !file.type.startsWith('image/'),
      );

      // Imagens passam pelo preview de anotação antes de subir; os demais
      // tipos seguem direto para o upload, como antes.
      if (images.length) {
        setAnnotationQueue((prev) => [...prev, ...images]);
      }
      if (others.length) {
        uploady.upload(others);
        onUploadStart?.();
      }
    },
    [onUploadStart, uploady],
  );

  const handleAnnotationConfirm = React.useCallback(
    async (dataUrl: string | null) => {
      if (!currentFile) return;
      let fileToUpload = currentFile;
      if (dataUrl) {
        const blob = await readFile(dataUrl);
        fileToUpload = await blobToFile(blob, currentFile.name);
      }
      uploady.upload([fileToUpload]);
      onUploadStart?.();
      setAnnotationQueue((prev) => prev.slice(1));
    },
    [currentFile, onUploadStart, uploady],
  );

  const handleAnnotationCancel = React.useCallback(() => {
    setAnnotationQueue((prev) => prev.slice(1));
  }, []);

  const uploadingItems = React.useMemo(
    () => files.filter((e) => e.state !== FILE_STATES.FINISHED),
    [files],
  );

  return (
    <Flex p="1" w="full" flexDir="column" gap="4">
      {isLoading && <Spinner m="5" mx="auto" />}
      <Dropzone
        onDrop={handleDrop}
        maxFiles={5}
        placeholderProps={{
          title: t('common.drag_drop_files_here'),
          subtitle: t('common.attach_files_subtitle'),
        }}
      />
      <List spacing="2" hidden={!uploadingItems.length}>
        {uploadingItems.map(renderUploadingItem)}
      </List>
      <List spacing="2" hidden={!items.length}>
        {items.map(renderItem)}
      </List>

      <PhotoAnnotatorModal
        isOpen={!!currentFile}
        photoSrc={currentFileUrl}
        onClose={handleAnnotationCancel}
        onConfirm={handleAnnotationConfirm}
      />
    </Flex>
  );
};
